import { Component, OnInit, Input, ElementRef, signal, ChangeDetectionStrategy, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { Panel } from "../s25-panel/s25.panels.component";
import { SeAboutOrgComponent } from "./organization/se.about.org.component";
import { S25ItemListComponent } from "../s25-item/s25.item.list.component";
import { StudentEngagementService } from "./student.engagement.service";
import { StudentEngagementOrg } from "./StudentEngagementOrg";
import { Item } from "../../pojo/Item";
import { S25PanelsModule } from "../s25-panel/s25.panels.module";
import { BudgetContainerComponent } from "./budget/budget.container.component";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../services/state/state.map";
import { SeOrgDetailsViewComponent } from "./organization/se.org.details.view.component";
import { LoginService } from "../../services/login.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { UserprefService } from "../../services/userpref.service";

type OrgDetailsSections = "details" | "budgets" | "events" | "members" | "website" | "contact";

@TypeManagerDecorator("s25-ng-se-org-home")
@Component({
    selector: "s25-ng-se-org-home",
    template: `
        @if (init()) {
            <h1>{{ orgDetails.name }}</h1>

            <a s25-ng-router-link [stateName]="routerLinkStateName">← Back to organizations list</a>

            <!-- Navigation buttons for different areas -->
            <div class="nav-buttons">
                @for (navButton of navButtons; track navButton.itemName) {
                    <button
                        class="aw-button"
                        [class.active]="activeSection() === navButton.section"
                        [title]="navButton.title"
                        (click)="activeSection.set(navButton.section)"
                    >
                        {{ navButton.itemName }}
                    </button>
                }
            </div>

            <!-- Content sections -->
            @if (orgDetails) {
                @switch (activeSection()) {
                    @case ("details") {
                        <div class="section-content">
                            <s25-ng-se-org-details-view
                                [orgId]="itemId"
                                [orgDetails]="orgDetails"
                            ></s25-ng-se-org-details-view>
                        </div>
                    }
                    @case ("budgets") {
                        <s25-ng-budget-container [orgId]="itemId"></s25-ng-budget-container>
                    }
                    @case ("events") {
                        <div class="section-content">
                            <h2>Events</h2>
                            @if (orgDetails.upcomingEvents && orgDetails.upcomingEvents.length) {
                                <h3>Upcoming Events</h3>
                                <ul>
                                    @for (event of orgDetails.upcomingEvents; track event.itemId) {
                                        <li>{{ event.title }}</li>
                                    }
                                </ul>
                            }
                        </div>
                    }
                    @case ("members") {
                        <div class="section-content">
                            <h2>Members</h2>
                            <s25-ng-panels [panels]="panels"></s25-ng-panels>
                        </div>
                    }
                    @case ("website") {
                        <div class="section-content">
                            <h2>Website</h2>
                            <p>Website management functionality will be implemented here.</p>
                        </div>
                    }
                    @case ("contact") {
                        <div class="section-content">
                            <h2>Contact Us</h2>
                            <p>Contact information and management will be implemented here.</p>
                        </div>
                    }
                }
            }
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        h1 {
            text-align: center;
            margin-bottom: 2rem;
            color: #333;
            font-size: 2.5rem;
        }

        .nav-buttons {
            display: flex;
            gap: 1rem;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            justify-content: center;
        }

        .aw-button {
            padding: 1rem 2rem;
            font-size: 1.1rem;
            font-weight: 600;
            border: 2px solid #e5e5e5;
            border-radius: 8px;
            background: white;
            color: #666;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 140px;
        }

        .aw-button:hover {
            border-color: #2196f3;
            color: #2196f3;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
        }

        .aw-button.active {
            background: #2196f3;
            border-color: #2196f3;
            color: white;
            box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
        }

        .section-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            padding: 2rem;
            margin-bottom: 2rem;
        }

        .section-content h2 {
            color: #333;
            margin-bottom: 1.5rem;
            font-size: 1.8rem;
        }

        .section-content h3 {
            color: #666;
            margin-bottom: 1rem;
            font-size: 1.3rem;
        }

        .section-content ul {
            list-style: none;
            padding: 0;
        }

        .section-content li {
            padding: 0.75rem;
            border-bottom: 1px solid #f0f0f0;
            color: #333;
        }

        .section-content li:last-child {
            border-bottom: none;
        }

        s25-ng-loading-inline-static {
            display: block;
            width: fit-content;
            margin: 0 auto;
        }

        @media (max-width: 768px) {
            h1 {
                font-size: 2rem;
            }

            .nav-buttons {
                flex-direction: column;
                align-items: stretch;
            }

            .aw-button {
                width: 100%;
                min-width: auto;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25PanelsModule,
        BudgetContainerComponent,
        S25RouterLinkDirective,
        SeOrgDetailsViewComponent,
        S25LoadingInlineModule,
    ],
    providers: [StudentEngagementService],
})
export class SeOrgHomeComponent implements OnInit {
    @Input() itemId: number;
    init = signal(false);

    constructor(private elementRef: ElementRef) {
        this.elementRef.nativeElement.angBridge = this;

        // Set up setter function for AngularJS to call
        this.itemIdSetter = (value: number) => {
            if (this.itemId !== value) {
                this.itemId = value;
                this.initializeComponent();
            }
        };
    }

    // Bridge function for AngularJS to call
    itemIdSetter: (value: number) => void;
    orgDetails: StudentEngagementOrg.details;

    navButtons: Array<S25ItemI & { section: OrgDetailsSections }> = [
        { itemName: "Details", destination: "", title: "View organization details", section: "details" },
        {
            itemName: "Budgets",
            destination: "",
            title: "Manage organization budgets and financial planning",
            section: "budgets",
        },
        { itemName: "Events", destination: "", title: "View and manage upcoming events", section: "events" },
        { itemName: "Members", destination: "", title: "Manage organization membership", section: "members" },
        { itemName: "Website", destination: "", title: "Manage organization website", section: "website" },
        { itemName: "Contact Us", destination: "", title: "Manage contact information", section: "contact" },
    ];

    panels: Panel[] = []; // Placeholder for panels, can be populated later
    activeSection = signal<OrgDetailsSections>("details");
    routerLinkStateName: S25States.Name;

    seService = inject(StudentEngagementService);

    async ngOnInit() {
        this.initializeComponent();
        const groupId = await UserprefService.getGroupId();
        this.seService.setGroupId(groupId);
    }

    private initializeComponent(): void {
        // Reset component state
        this.init.set(false);
        this.orgDetails = null;
        this.panels = [];

        if (this.itemId) {
            this.getData()
                .then(() => {
                    this.createPanels();
                    this.init.set(true);
                })
                .catch((error) => {
                    console.error("Error loading organization details:", error);
                });
        } else {
            console.warn("No itemId provided, cannot load organization details.");
        }
    }

    async getData(): Promise<void> {
        const [loggedIn, orgDetails] = await Promise.all([
            LoginService.getLoggedIn(),
            StudentEngagementService.getOrganizationDetails(this.itemId),
        ]);
        this.orgDetails = orgDetails;
        this.routerLinkStateName = loggedIn
            ? S25States.names.studentEngagement
            : S25States.names.studentEngagementLanding;
    }

    createPanels(): void {
        // This method can be used to create panels dynamically based on the organization details
        this.panels = [
            {
                header: "About Us",
                id: "about-us",
                section: 0,
                sectionOrder: 1,
                content: {
                    component: SeAboutOrgComponent,
                    inputs: {
                        mission: this.orgDetails.mission,
                        imageUrl: this.orgDetails.imageAttr,
                    },
                },
            },
            {
                header: "Members",
                id: "members",
                section: 1,
                sectionOrder: 0,
                content: {
                    component: S25ItemListComponent,
                    inputs: {
                        items: this.orgDetails.members,
                        itemType: Item.Ids.Contact,
                    },
                },
            },
        ];
    }
}
